import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import colour from 'client/utils/constants/GDS/js/color';

const defaultSize = 30;

const CircleIcon = ({ fill, radius, stroke, scale, ...props }) => {
    const canvasRef = useRef();
    const [size, setSize] = useState(defaultSize);

    useEffect(() => {
        updateSize();
        updateCanvas();
    }, [fill, radius, stroke, scale]);

    const updateSize = () => {
        if (radius * 2 >= size) {
            setSize(radius * 2);
        } else {
            setSize(defaultSize);
        }
    };

    const updateCanvas = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                // Reset canvas when updating, otherwise previous drawings remain
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                ctx.beginPath();
                ctx.strokeStyle = stroke?.color;
                ctx.lineCap = stroke?.lineCap;
                ctx.lineJoin = stroke?.lineJoin;
                if (stroke?.lineDash) {
                    ctx.setLineDash(stroke?.lineDash);
                }
                ctx.setMiterLimit = stroke?.miterLimit;
                ctx.lineDashOffset = stroke?.lineDashOffset;
                ctx.lineWidth = stroke?.width;
                ctx.scale(scale, scale);

                ctx.arc((size / 2) * scale, (size / 2) * scale, radius, 0, 2 * Math.PI);
                ctx.stroke();
                if (fill?.color) {
                    ctx.fillStyle = fill?.color;
                    ctx.fill();
                }
            }
        }
    };

    return (
        <canvas
            ref={canvasRef}
            width={size}
            height={size}
            data-testid={props['data-testid']}
        />
    );
};

CircleIcon.propTypes = {
    fill: PropTypes.shape({
        color: PropTypes.string
    }),
    radius: PropTypes.number,
    stroke: PropTypes.shape({
        color: PropTypes.string,
        lineCap: PropTypes.oneOf(['butt', 'round', 'square']),
        lineJoin: PropTypes.oneOf(['bevel', 'round', 'miter']),
        lineDash: PropTypes.arrayOf(PropTypes.number),
        lineDashOffset: PropTypes.number,
        miterLimit: PropTypes.number,
        width: PropTypes.number
    }),
    scale: PropTypes.number,
    'data-testid': PropTypes.string
};

CircleIcon.defaultProps = {
    fill: {
        color: colour.black
    },
    radius: 7,
    stroke: {
        color: colour.black,
        lineCap: 'round',
        lineJoin: 'round',
        lineDashOffset: 0,
        miterLimit: 10,
        width: 3
    },
    displacement: [0, 0],
    rotation: 0,
    scale: 1
};

export default CircleIcon;
